"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { BadgeCheck, Heart, ImageOff, Star } from "lucide-react";
import { Avatar } from "@/features/home-v2/ui";
import type { CollectionModel } from "./collectionView";
import type { useCollectionToggles } from "./collectionView";
type Toggles = ReturnType<typeof useCollectionToggles>;

/** The collection's mark; a quiet placeholder when it has none or its file fails to load. */
export function CollectionLogo(t0) {
  const $ = _c(10);
  const {
    v,
    size,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [failed, setFailed] = useState(false);
  const t2 = `v2-clx-logo ${className}`;
  let t3;
  if ($[0] !== size) {
    t3 = {
      inlineSize: size,
      blockSize: size
    };
    $[0] = size;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== failed || $[3] !== size || $[4] !== v.logo) {
    t4 = v.logo && !failed ? <Image src={v.logo} alt="" width={size * 2} height={size * 2} loading="lazy" onError={() => setFailed(true)} /> : <ImageOff aria-hidden={true} />;
    $[2] = failed;
    $[3] = size;
    $[4] = v.logo;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <span className={t2} style={t3}>{t4}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function CollectionName(t0) {
  const $ = _c(8);
  const {
    v,
    lang
  } = t0;
  let t1;
  if ($[0] !== v.name) {
    t1 = <span>{v.name}</span>;
    $[0] = v.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== lang || $[3] !== v.verified) {
    t2 = v.verified ? <BadgeCheck className="v2-clx-verified" aria-label={lang === "fa" ? "\u062A\u0627\u06CC\u06CC\u062F\u200C\u0634\u062F\u0647" : "Verified"} /> : null;
    $[2] = lang;
    $[3] = v.verified;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="v2-clx-name">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** The creator, linked to their profile when there is a handle. It sits above a card's stretched link. */
export function CollectionCreator(t0) {
  const $ = _c(13);
  const {
    v,
    lang
  } = t0;
  if (!v.creator) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== v.avatar || $[2] !== v.creator) {
    t1 = <span className="v2-clx-creator-av"><Avatar name={v.creator} src={v.avatar} size="fill" /></span>;
    $[1] = v.avatar;
    $[2] = v.creator;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== v.creator) {
    t2 = <span className="v2-clx-creator-name">{v.creator}</span>;
    $[4] = v.creator;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const inner = t3;
  let t4;
  if ($[9] !== inner || $[10] !== lang || $[11] !== v.handle) {
    t4 = v.handle ? <Link className="v2-clx-creator v2-focus" href={`/${lang}/artists/${v.handle}`}>{inner}</Link> : <span className="v2-clx-creator">{inner}</span>;
    $[9] = inner;
    $[10] = lang;
    $[11] = v.handle;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export function ChainChip(t0) {
  const $ = _c(3);
  const {
    v
  } = t0;
  let t1;
  if ($[0] !== v.chain || $[1] !== v.chainKey) {
    t1 = v.chain ? <span className="v2-chip v2-chip--chain" data-chain={v.chainKey}>{v.chain}</span> : null;
    $[0] = v.chain;
    $[1] = v.chainKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Like with its count, and watchlist — both above the stretched link. */
export function CollectionToggles(t0) {
  const $ = _c(17);
  const {
    t,
    num,
    only
  } = t0;
  let t1;
  if ($[0] !== num || $[1] !== only || $[2] !== t.likeCount || $[3] !== t.likeLabel || $[4] !== t.likePending || $[5] !== t.liked || $[6] !== t.toggleLike) {
    t1 = only === "watch" ? null : <button type="button" className="v2-clx-toggle v2-focus" onClick={t.toggleLike} aria-pressed={t.liked} aria-busy={t.likePending} aria-label={t.likeLabel}><Heart aria-hidden={true} />{t.likeCount > 0 ? <span>{num.format(t.likeCount)}</span> : null}</button>;
    $[0] = num;
    $[1] = only;
    $[2] = t.likeCount;
    $[3] = t.likeLabel;
    $[4] = t.likePending;
    $[5] = t.liked;
    $[6] = t.toggleLike;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Star aria-hidden={true} />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t.toggleWatch || $[10] !== t.watchLabel || $[11] !== t.watchPending || $[12] !== t.watched) {
    t3 = <button type="button" className="v2-clx-toggle v2-focus" onClick={t.toggleWatch} aria-pressed={t.watched} aria-busy={t.watchPending} aria-label={t.watchLabel}>{t2}</button>;
    $[9] = t.toggleWatch;
    $[10] = t.watchLabel;
    $[11] = t.watchPending;
    $[12] = t.watched;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t3) {
    t4 = <span className="v2-clx-toggles">{t1}{t3}</span>;
    $[14] = t1;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}